/*
 * The shell's copy. Everything the frame around a screen can say: the landmarks, the navigation,
 * the connection banner, the quick switcher, the shortcut sheet and the one page that belongs to no
 * area (404). Each area keeps its own catalogue beside it; core/i18n/index.ts merges them into the
 * one typed catalogue `t()` is checked against.
 */

export const en = {
	"shell.skip.toMain": "Skip to the main area",

	"shell.region.rail": "Areas",
	"shell.region.sidebar": "Navigation",
	"shell.region.main": "Main area",
	"shell.region.details": "Details",

	// The rail is places, not tools (UX §1.1): five of these are rows on it, the rest are entries
	// of the account menu and of the quick switcher, which is the fallback for everything the rail
	// no longer carries.
	"shell.nav.home": "Home",
	"shell.nav.activity": "Activity",
	// The AI chats place. Only its label is core's — the rest of that place's copy is the module's
	// own namespace — because the rail is a list of places the shell draws, and it draws them before
	// any module's chunk has arrived.
	"shell.rail.aichat": "AI Chats",
	// …and its caption on the phone's bottom bar, where "AI Chats" runs into its neighbour.
	"shell.rail.aichat.bar": "AI",
	// The drive's place; its label is core's for the AI chats' reason above.
	"shell.rail.drive": "Drive",
	"shell.nav.search": "Search",
	// The rail's word for the place, not the place's own title: "Administration" is 87 pixels wide
	// at --text-xs and the rail's item is 64, so it could only ever be drawn broken across two
	// lines in the middle of the word. The page it opens is headed "Administration" (admin.title),
	// which is the same place written out.
	"shell.nav.admin": "Admin",
	"shell.nav.people": "People & groups",
	"shell.nav.settings": "Settings",
	// The rail entry of a place with waiting items reads as one name — a bare number beside an icon
	// is not a name a screen reader can act on (spec 09 §4).
	"shell.nav.unread": "{label}, {count} unread",

	// One sentence for the whole column when every section is empty; the door under it is the
	// module's (`SidebarSection.Door`). It names what is empty rather than saying "nothing": on a new
	// member's first screen it stood beside Home's "Nothing to catch up on yet." and the two read as
	// the same news said twice (N5-R1-15).
	"shell.sidebar.empty": "No conversations yet.",

	// ---- K-01: the shell frame — the rail's head and account entry, the folded sidebar, Alt+← ----
	//
	// The account entry is named by the person and by what it opens, because the face alone is what
	// a sighted reader sees. "You" is the bottom bar's caption for it: a display name is the one
	// caption the copy does not control (D-06 §2.15). The hidden count is what a folded section's
	// heading appends for the rows it keeps out of sight.
	"shell.nav.account": "{name}, account menu",
	// The pill under a guest's handle in their own account menu.
	"shell.guest.label": "Guest account",
	"shell.nav.brand": "teamBase — Home",
	"shell.nav.you": "You",
	"shell.shortcut.back": "Back to where you were",
	"shell.sidebar.hidden": "· {count}",
	// The head of a folded column brought over a page (G-02 §2): it keeps the list standing beside
	// the page from now on; the page's own toggle folds it again.
	"shell.sidebar.keepOpen": "Keep the list open",

	// The details column (UX §2). It is empty until something is asked to be shown in it, so there
	// is no sentence for an empty one; what is left is the way out and the names of core's own
	// views, which the pane draws as its heading and the sheet as its dialog title.
	"shell.details.close": "Close details",
	"shell.details.profile": "Profile",

	"shell.resize.sidebar": "Navigation width",
	"shell.resize.details": "Details column width",

	// Two sentences for one outage: the first is polite and the second, after half a minute, is
	// the one a screen reader interrupts for — by then it is no longer a hiccup (X-A2 A5).
	"shell.connection.offline": "No connection. Trying again.",
	"shell.connection.stillOffline":
		"Still no connection. What you write is kept and sent when it comes back.",

	// K-07 — the quick switcher, which is a command palette and not a dialog (VISUAL §6.13): the
	// groups its rows stand under, what the field says before anything is typed, and the three keys
	// its footer prints. `label` is the field's accessible name; the footer is the same sentence in
	// marks, so `hint` is what a reader who is not looking at it gets instead.
	"shell.quickSwitcher.empty": "Nothing found.",
	"shell.quickSwitcher.group.channels": "Channels",
	"shell.quickSwitcher.group.people": "People",
	"shell.quickSwitcher.group.places": "Places",
	"shell.quickSwitcher.group.recent": "Recent",
	"shell.quickSwitcher.hint": "Type to filter, pick with the arrow keys, open with Enter.",
	"shell.quickSwitcher.key.close": "close",
	"shell.quickSwitcher.key.escape": "esc",
	"shell.quickSwitcher.key.move": "move",
	"shell.quickSwitcher.key.open": "open",
	"shell.quickSwitcher.label": "Search term",
	"shell.quickSwitcher.placeholder": "Jump to a channel, a person or a page",
	"shell.quickSwitcher.results": "{count} results",
	"shell.quickSwitcher.results.one": "{count} result",
	"shell.quickSwitcher.title": "Quick switch",

	"shell.shortcuts.title": "Keyboard shortcuts",
	"shell.shortcuts.scope.global": "Everywhere",
	"shell.shortcuts.scope.conversation": "In a conversation",
	"shell.shortcuts.scope.messageList": "In the message list",
	"shell.shortcuts.scope.composer": "In the message box",
	"shell.shortcuts.scope.writing": "Writing a message",
	"shell.shortcuts.scope.activity": "In Activity",
	// Between two chords that do the same thing; the sheet never prints two chips side by side
	// without saying which relation they are in.
	"shell.shortcuts.or": "or",

	"shell.shortcut.quickSwitcher": "Quick switch",
	"shell.shortcut.search": "Open search",
	"shell.shortcut.activity": "Open Activity",
	"shell.shortcut.sheet": "Show keyboard shortcuts",
	"shell.shortcut.regionNext": "Go to the next area",
	"shell.shortcut.regionPrev": "Go to the previous area",

	// The writing chords that are not registry bindings: they are the editor's own keymap
	// (core/editor/keymap.ts) and the browser's paste, and until they stood here the sheet
	// documented ten of the sixteen keys a person can press (X-A2 D8). The nine formatting chords
	// beside them are generated from SHORTCUT_CHORDS and need no copy of their own.
	"shell.shortcut.editLast": "Edit your last message (in an empty box)",
	"shell.shortcut.listIn": "Move a list item in one level",
	"shell.shortcut.listOut": "Move a list item out one level",
	"shell.shortcut.newline": "Start a new line",
	"shell.shortcut.pastePlain": "Paste without formatting",
	"shell.shortcut.redo": "Redo",
	"shell.shortcut.send": "Send the message",
	"shell.shortcut.sendAnywhere": "Send from anywhere, inside a code block included",
	"shell.shortcut.undo": "Undo",

	// ---- L-01: the toast stack ----
	//
	// A toast's sentence is never its own: every one of them repeats the words the action already
	// announced (`chat.message.copied` is the first), so the two halves of one report cannot drift
	// apart. What is left here is the card's own chrome.
	"shell.toast.dismiss": "Dismiss message",
	"shell.toast.more": "+{count} more",

	"shell.notFound.title": "This page does not exist",
	// Module-neutral on purpose: this is the screen behind *every* address that resolves to nothing —
	// a renamed document's old link as much as an archived channel's — and naming one module's things
	// made the commonest broken link read as a fault of something else.
	"shell.notFound.body": "The link leads nowhere. The page may have been moved or archived.",
	"shell.notFound.home": "Go to Home",

	// The one line of the state that has no screen behind it: the boot itself failed, so there is no
	// router, no shell and no toast to say it in (`main.tsx`, `core/shell/bootFailure.ts`). It names
	// the only thing that helps — a boot fails on a chunk or the session request, and both succeed on
	// the next attempt — and it says nothing about what failed, because at that point nothing knows.
	"shell.boot.failed": "teamBase could not load. Reload the page.",

	// ---- B5-Z: the announcer's two live regions ----
	//
	// They stand at the end of every document and are otherwise anonymous in a screen reader's
	// landmark list, where two unnamed regions are two things nobody can skip past (X-A2 P12).
	"shell.announcer.alerts": "Alerts",
	"shell.announcer.polite": "Announcements",

	// ---- O-01: Home as the catch-up screen, the first run per role, the 404 (VISUAL.md §7.8,
	// §7.16, §7.17; the copy is D-10 §2) ----
	//
	// Home greets by the hour of the account's own zone and by the first name, says what is waiting
	// in one line, and hands the rest to the modules (`homeSections`). Its two empty states differ:
	// a reader in no conversation is offered the modules' doors, a reader who has read everything
	// the rocket and the channel where everybody is.
	//
	// The first run is one screen per role. A member's holds the three questions of a first minute
	// — where the team is, who is here, where one's own name lives — each ending in a door; an
	// administrator's is the checklist, which ticks from the model and says what is left to do on an
	// instance nobody has set up yet. Both are seen once per account (`core.shell.seen_welcome`).
	"shell.firstRun.admin.lead": "Three steps and the team can move in.",
	"shell.firstRun.admin.title": "Welcome, {name}. Let’s set this place up.",
	"shell.firstRun.dismiss": "Got it",
	// A guest's first screen is the member's with the guest's own lead: a guest sees the
	// conversations they are added to and the channels of their groups, and is told so once.
	"shell.firstRun.guest.lead":
		"You are a guest here: you see the conversations you are added to and the channels of your groups.",
	"shell.firstRun.guest.whereNone":
		"Nobody has added you to a channel yet. Whoever invited you can, and the channels of your groups are under Browse channels.",
	"shell.firstRun.hint": "{mod}+K finds any conversation or person · {mod}+/ lists every shortcut",
	"shell.firstRun.member.lead": "This is where the team talks.",
	"shell.firstRun.member.title": "Welcome, {name}.",
	"shell.firstRun.people": "See people",
	"shell.firstRun.peopleCount": "{count} people",
	"shell.firstRun.peopleCount.one": "{count} person",
	"shell.firstRun.profile": "Complete profile",
	"shell.firstRun.say": "Say who you are",
	"shell.firstRun.sayDetail": "Your picture, your title, your time zone.",
	"shell.firstRun.where": "Where everybody is",
	"shell.firstRun.whereLine": "{channel} · {members}",
	"shell.firstRun.whereNone": "Channels show up on the left as soon as you join one.",
	"shell.firstRun.who": "Who is here",
	"shell.home.greeting.afternoon": "Good afternoon, {name}.",
	"shell.home.greeting.evening": "Good evening, {name}.",
	"shell.home.greeting.morning": "Good morning, {name}.",
	// A guest in no conversation yet: nothing of the team's is theirs until somebody adds them, so
	// the sentence says who can, and where their groups' channels are.
	"shell.home.guestEmpty.lead":
		"Whoever invited you can add you to a channel. The channels of your groups are under Browse channels.",
	"shell.home.guestEmpty.title": "Nobody has added you to a conversation yet.",
	"shell.home.inConversations": "in {count} conversations",
	"shell.home.inConversations.one": "in {count} conversation",
	"shell.home.mentions": "{count} mentions",
	"shell.home.mentions.one": "{count} mention",
	"shell.home.nothingJoined.lead": "Channels you join and messages you get show up here.",
	"shell.home.nothingJoined.title": "Nothing to catch up on yet.",
	"shell.home.open": "Open {channel}",
	"shell.home.unread": "{count} unread",
	"shell.home.upToDate.lead": "Nothing new since you were last here.",
	"shell.home.upToDate.title": "You are up to date.",
	"shell.setup.channel": "Create the channel everybody joins",
	"shell.setup.channel.hint": "The + beside Channels, on the left.",
	"shell.setup.done": "done",
	"shell.setup.hello": "Write the first message",
	// The row's place for a door, on an instance that has no channel for it to point at yet.
	"shell.setup.hello.hint": "In the channel, once there is one.",
	"shell.setup.invite": "Invite the team",
	"shell.setup.invite.action": "Invite people",
	"shell.setup.optional": "optional",
	"shell.setup.picture": "Set your picture",
	"shell.setup.picture.action": "Open your profile",
	"shell.setup.progress": "{done} of {total} done",
	"shell.setup.title": "Set this place up",
	"shell.setup.todo": "still to do",
	// ---- V-01: the details column's tab strip ----
	//
	// Views of one group — About, Files, Pinned — are tabs of one column, and a screen reader is
	// told the strip's name once before it walks the tabs.
	"shell.details.tabs": "Details sections",

	// ---- E-09: the phone's conversation list ----
	//
	// One word for the bar's entry and for the sheet it opens, because they are one thing: below
	// 768 px the conversation list is not a column but a surface the reader asks for (VISUAL.md
	// §7.19). The count it carries is `shell.nav.unread`, the same name-with-its-count every waiting
	// place on the rail wears.
	"shell.nav.chats": "Chats",

	// ---- P-2: what the browser tab says ----
	//
	// The two pieces of the tab's title, built in `core/shell/usePageTitle.ts`. The name of what is
	// open comes first, because a row of tabs shows only the first few characters of each and the
	// product's name is the same on all of them; the count of what is waiting stands in front of
	// everything, where a browser's own notification badges sit.
	"shell.pageTitle": "{title} · teamBase",
	"shell.tabWaiting": "({count}) {title}",
} as const satisfies Record<string, string>
